import React, { useEffect, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import {
  BulbOutlined, CheckOutlined, HistoryOutlined, StopOutlined, WarningOutlined,
} from "@ant-design/icons";

import config from "../../../../config";
import "./carrierPlaybook.css";

/**
 * How to actually get this task done with this carrier.
 *
 * The card above it says who to call and whether it's a portal or a phone job.
 * This is the part that used to live only in people's heads — that Manulife's
 * line never reaches a human unless you enter nothing and press 5.
 *
 * Renders NOTHING unless the server returns a playbook. The endpoint is gated to
 * super admins and FTC personnel, so for an office user the fetch 403s and the
 * card looks exactly as it did before this feature existed. That absence is the
 * gate working, not an error.
 */

function authHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  return user && user.key ? { Authorization: "Token  " + user.key } : {};
}

function IvrSteps({ steps }) {
  if (!steps || !steps.length) return null;
  return (
    <ol className="cpb-ivr">
      {steps.map(function (step, i) {
        return (
          <li key={i}>
            <span className="cpb-ivr-what">{step.description || "Menu"}</span>
            {step.digits ? <span className="cpb-ivr-digits">press {step.digits}</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A titled block — and the ONLY heading level in this panel.
 *
 * There used to be two: a pair of section headers ("How to get it" / "How to
 * enter it") over a set of quieter block labels. Two levels in a 300px rail buys
 * nothing — the blocks are already ordered by the two moments, so the grouping
 * survives without headers announcing it, and one heading style means nothing
 * has to be decoded before it can be read.
 */
function Block({ label, children }) {
  return (
    <div className="cpb-block">
      <div className="cpb-sec">
        <span className="cpb-sec-mark" aria-hidden="true" />
        {label}
      </div>
      {children}
    </div>
  );
}

// Full sentences. An earlier pass compressed these to "names, not codes" and the
// meaning went with the words — the reader could not tell what the fragment was
// even about.
const GRANULARITY_TEXT = {
  per_name: "They read out procedure names, not codes.",
  per_code: "They quote coverage per procedure code.",
  per_category: "They give coverage per category, not per procedure.",
  mixed: "They mix codes, names and categories.",
};

const FREQUENCY_TEXT = {
  rolling_12m: "Frequency runs rolling from the last service date.",
  calendar_year: "Frequency runs on the calendar year.",
  benefit_year: "Frequency runs on the plan's benefit year.",
  varies: "Frequency basis varies by plan — check each one.",
};

const BENEFIT_YEAR_TEXT = {
  anniversary: "The benefit year starts on the policy anniversary, not in January.",
  varies: "The benefit year start varies by plan.",
};

/**
 * What you are going to be told, and in what shape.
 *
 * Lives in "how to enter it" rather than at the top of the panel: it is what you
 * need at the moment you are typing a number in, not while you are still dialling.
 * Reads as advice for that moment, which is also why it no longer duplicates the
 * ask-for-these checklist — the same fact at two different moments is not a
 * repetition, it is the point.
 */
function ReportingStyle({ style }) {
  if (!style) return null;
  const lines = [
    GRANULARITY_TEXT[style.granularity],
    // Only worth a line when it ADDS something the granularity did not say.
    style.quotes_cda_codes === "no" && style.granularity !== "per_name"
      ? "They will not give you a CDA code, even if you ask."
      : null,
    FREQUENCY_TEXT[style.frequency_basis],
    BENEFIT_YEAR_TEXT[style.benefit_year_start],
  ].filter(Boolean);
  if (!lines.length) return null;
  return (
    <Block label="What you'll get">
      {lines.map(function (line, i) {
        return <p className="cpb-fact" key={i}>{line}</p>;
      })}
    </Block>
  );
}

/**
 * Their wording, mapped to what we store it as.
 *
 * This was in the model from the start and had never been rendered anywhere —
 * which made it the biggest gap in the panel, because it is the one piece of
 * knowledge that answers "the rep just said 'routine cleaning', what do I put in
 * the table?".
 */
function TermLexicon({ lexicon }) {
  const terms = Object.keys(lexicon || {});
  if (!terms.length) return null;
  return (
    <Block label="What their words mean">
      {terms.map(function (term) {
        const target = lexicon[term] || {};
        return (
          <div className="cpb-term" key={term}>
            <span className="cpb-term-said">“{term}”</span>
            <span className="cpb-term-arrow" aria-hidden="true">→</span>
            <span className="cpb-term-is">{target.code || target.category}</span>
          </div>
        );
      })}
    </Block>
  );
}

/**
 * The things this carrier won't tell you unless you ask.
 *
 * Deliberately the FIRST thing in the panel and rendered as a checklist: it is
 * read before the call starts, and every line on it is a second call avoided.
 * Ticking is local and throwaway — this is a memory aid during one call, not
 * state anyone needs afterwards.
 */
function MustAsk({ items }) {
  const [done, setDone] = useState({});
  if (!items || !items.length) return null;
  return (
    <Block label="Make sure you ask">
      <ul className="cpb-mustask-list">
        {items.map(function (item, i) {
          return (
            <li
              key={i}
              className={"cpb-mustask-item" + (done[i] ? " is-done" : "")}
              onClick={function () {
                setDone(function (prev) { return Object.assign({}, prev, { [i]: !prev[i] }); });
              }}
              title="Tick off while you're on the call"
            >
              <span className="cpb-tick" aria-hidden="true">
                {done[i] ? <CheckOutlined /> : null}
              </span>
              <span>{item}</span>
            </li>
          );
        })}
      </ul>
    </Block>
  );
}

// A rule's scope, as the office says it rather than as we store it.
function ruleScope(rule) {
  const scope = rule.applies_to;
  if (Array.isArray(scope)) return scope.join(" + ");
  return scope || "";
}

/**
 * Carrier conventions that QUALIFY whatever number you are given.
 *
 * Grouped by what they apply to, so the rule about orthodontics is next to the
 * orthodontics row being typed rather than in a list someone reads once.
 */
function IntakeRules({ rules }) {
  if (!rules || !rules.length) return null;
  const groups = {};
  rules.forEach(function (rule) {
    if (!rule || !rule.text) return;
    const key = ruleScope(rule) || "Whole plan";
    (groups[key] = groups[key] || []).push(rule);
  });
  const keys = Object.keys(groups);
  if (!keys.length) return null;

  // Label, then text. Nothing else: a scope label AND a left border was two
  // decorations doing one job, and the border made each rule look like a pull
  // quote competing with the checklist above it.
  return (
    <Block label="Watch out for">
      {keys.map(function (key) {
        return (
          <div className="cpb-rule-group" key={key}>
            {/* Scope above rather than beside: at 300px a two-column row shreds
                both halves into two-word ribbons, and what a rule APPLIES TO is
                what you scan for when you are on that category's row. */}
            <div className="cpb-rule-scope">{key}</div>
            {groups[key].map(function (rule, i) {
              return <div className="cpb-rule" key={i}>{rule.text}</div>;
            })}
          </div>
        );
      })}
    </Block>
  );
}

export default function CarrierPlaybookPanel({ policy, taskType }) {
  // The insurance modal's policy payload carries `carrier_id`, NOT `carrier_code`
  // (see the serializer: carrier_id / carrier_display_name / carrier_phone_number
  // …). Keying off the code silently produced `undefined`, so the fetch never
  // fired and the panel never appeared anywhere. The endpoint accepts either.
  const carrierId = policy && policy.carrier_id;
  const carrierCode = policy && policy.carrier_code;
  const carrierParam = carrierId
    ? `carrier_id=${encodeURIComponent(carrierId)}`
    : (carrierCode ? `carrier_code=${encodeURIComponent(carrierCode)}` : null);
  const [books, setBooks] = useState(null);      // null = not loaded / not permitted
  // Tasks this carrier DOES have guidance for. Without this an empty Estimates
  // tab reads as "the feature is broken" while a full breakdown playbook sits
  // one tab away.
  const [otherTasks, setOtherTasks] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(
    function () {
      if (!carrierParam || !taskType) { setBooks(null); return undefined; }
      let cancelled = false;
      (async function () {
        try {
          const res = await fetch(
            `${config.apiGateway.URL}/clinics/carrier-playbook/`
            + `?${carrierParam}&task_type=${encodeURIComponent(taskType)}`,
            { method: "GET", headers: authHeader() },
          );
          if (cancelled) return;
          // 403 is the ordinary answer for most users. Not an error to report.
          if (!res.ok) { setBooks(null); return; }
          const body = await res.json();
          setBooks((body && body.playbooks) || []);
          setOtherTasks((body && body.other_tasks) || []);
        } catch (e) {
          if (!cancelled) setBooks(null);
        }
      })();
      return function () { cancelled = true; };
    },
    [carrierParam, taskType, reloadTick],
  );

  async function submitReport(worked) {
    if (!worked && !reportText.trim()) {
      message.warning("Say what you had to do instead, so the next person knows.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${config.apiGateway.URL}/clinics/carrier-task-report/`, {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, authHeader()),
        body: JSON.stringify({
          carrier_id: carrierId,
          carrier_code: carrierCode,
          task_type: taskType,
          worked: worked,
          what_happened: reportText.trim(),
          policy_id: policy && policy.id,
        }),
      });
      if (res.ok) {
        const body = await res.json();
        if (body.deferred) {
          message.warning("Saved, but we couldn't update the guidance just now. It'll be retried.");
        } else if (body.applied) {
          message.success("Updated to v" + body.version + " — " + (body.change_summary || "thanks."));
        } else {
          message.success(worked ? "Thanks — noted." : "Thanks — nothing needed changing.");
        }
        setReportOpen(false);
        setReportText("");
        // Pull the new text straight back in, so the contributor SEES it land.
        setReloadTick(function (n) { return n + 1; });
      } else {
        message.error("Couldn't send that. Please try again.");
      }
    } catch (e) {
      message.error("Couldn't send that. Please try again.");
    } finally {
      setSending(false);
    }
  }

  // Defined before the early returns: both the "nothing written yet" state and
  // the populated panel offer it, and a hook-bearing component cannot declare it
  // after a conditional return.
  const reportModal = (
    <Modal
      open={reportOpen}
      onCancel={function () { setReportOpen(false); }}
      title={books && books.length ? "What actually happened?" : "What do you know?"}
      footer={null}
      destroyOnClose
    >
      <p className="cpb-modal-lede">
        Whatever you write here updates the guidance for everyone, so the next
        person doesn't have to work it out again. Portal or phone — whatever the
        route is, anything it can't give you, <strong>how they report coverage</strong>,
        and anything you had to ask for.
      </p>
      {/* The one way this feature can make things worse: a plan value written
          down as a carrier fact is wrong for every other patient, and nothing
          downstream can tell the difference. */}
      <p className="cpb-modal-lede cpb-modal-caveat">
        Write what is true of this <em>carrier</em>, not of this patient's plan.
        "They report frequency on a rolling basis" helps everyone; "this plan
        allows 2 per year" would be wrong for the next patient.
      </p>
      <Input.TextArea
        value={reportText}
        onChange={function (e) { setReportText(e.target.value); }}
        placeholder={"e.g. They only read out procedure names, never codes \u2014 you have to ask. "
                     + "Or: their portal shows the annual maximum but never frequency limits."}
        autoSize={{ minRows: 4, maxRows: 10 }}
        maxLength={4000}
      />
      <div className="cpb-modal-actions">
        {books && books.length ? (
          <Button onClick={function () { submitReport(true); }} disabled={sending}>
            It worked as written
          </Button>
        ) : null}
        <Button type="primary" loading={sending} onClick={function () { submitReport(false); }}>
          {books && books.length ? "Send correction" : "Add it"}
        </Button>
      </div>
    </Modal>
  );

  if (books === null) return null;      // not permitted — render nothing at all

  // Permitted, but nobody has written anything for this carrier and task. This is
  // the majority case today (29 of 48 carriers have no method even recorded), and
  // without an affordance here the knowledge base can never be started from the
  // one screen where the people who know are actually working.
  if (!books.length) {
    return (
      <div className="cpb cpb--empty">
        <div className="cpb-head">
          <BulbOutlined className="cpb-head-ico" />
          <span className="cpb-head-title">How to get this</span>
        </div>
        <div className="cpb-empty-text">
          Nothing written down for {taskType === "estimate" ? "estimates" : "breakdowns"} with
          this carrier yet.
          {otherTasks.length ? (
            <span className="cpb-elsewhere">
              {" "}There is guidance under{" "}
              <strong>{otherTasks.map(function (t) {
                return t === "general" ? "general notes" : t + "s";
              }).join(" and ")}</strong>.
            </span>
          ) : null}
          <button
            type="button"
            className="cpb-report"
            onClick={function () { setReportOpen(true); }}
          >
            Add what you know
          </button>
        </div>
        {reportModal}
      </div>
    );
  }

  return (
    <div className="cpb">
      {/* No panel-level header any more: the sections below name themselves, and
          "How to get this" sitting directly above "How to get it" read as a
          stutter. The empty state keeps its header — there it is the only label
          there is. */}
      {books.map(function (book) {
        // The two moments the FTC actually has, in order. Every field belongs to
        // exactly one of them:
        //
        //   GET  — availability, limitations, instructions, ivr_sequence,
        //          verification_fields, must_ask, quirks
        //   ENTER — reporting_style, term_lexicon, intake_rules
        //
        // Splitting by moment rather than by where the data happens to live is
        // the whole point: on a call you need one half, and while typing the
        // numbers in you need the other.
        return (
          <div className="cpb-book" key={book.id}>
            {/* A carrier-wide entry is labelled, because "always true here" and
                "true for this task" are different claims. */}
            {book.task_type === "general" ? (
              <div className="cpb-scope">Applies to this carrier generally</div>
            ) : null}

            {/* Ordered by the two moments — everything you need to GET the
                information, then everything you need to ENTER it. The order
                carries that grouping; it does not need announcing. */}

            {/* Whether it can be done at all, and what it won't give you, come
                FIRST — they are what stop a wasted attempt. These two keep their
                callout treatment rather than becoming labelled blocks: amber
                means "this route cannot do that", and it is the only thing on
                the panel that should stop you reading in order. */}
            {book.availability === "unavailable" ? (
              <div className="cpb-unavailable">
                <StopOutlined /> No known way to do this with this carrier.
              </div>
            ) : null}

            {book.limitations ? (
              <div className="cpb-limits">
                <WarningOutlined className="cpb-limits-ico" />
                <div>
                  <div className="cpb-limits-label">
                    {book.availability === "partial" ? "Only partly possible" : "Won't give you"}
                  </div>
                  {book.limitations}
                </div>
              </div>
            ) : null}

            {book.instructions ? (
              <Block label={book.ivr_sequence && book.ivr_sequence.length
                ? "On the phone" : "Where to look"}>
                <div className="cpb-instructions">{book.instructions}</div>
              </Block>
            ) : null}

            {book.ivr_sequence && book.ivr_sequence.length ? (
              <Block label="Phone menu">
                <IvrSteps steps={book.ivr_sequence} />
              </Block>
            ) : null}

            {book.verification_fields && book.verification_fields.length ? (
              <Block label="They'll ask you for">
                <div className="cpb-verify">
                  {book.verification_fields.map(function (f, i) {
                    return <span className="cpb-chip" key={i}>{String(f).replace(/_/g, " ")}</span>;
                  })}
                </div>
              </Block>
            ) : null}

            {/* Last of the getting-it blocks: it is what you say once you are
                already through to someone. */}
            <MustAsk items={book.must_ask} />

            {book.quirks ? (
              <Block label="Also worth knowing">
                <div className="cpb-quirks">{book.quirks}</div>
              </Block>
            ) : null}

            <ReportingStyle style={book.reporting_style} />
            <TermLexicon lexicon={book.term_lexicon} />
            <IntakeRules rules={book.intake_rules} />

            <div className="cpb-foot">
              <span className="cpb-meta">
                <HistoryOutlined /> v{book.version}
                {book.confirmations ? ` · confirmed ${book.confirmations}×` : ""}
              </span>
              <button
                type="button"
                className="cpb-report"
                onClick={function () { setReportOpen(true); }}
              >
                This wasn't right
              </button>
            </div>
          </div>
        );
      })}

      {reportModal}
    </div>
  );
}
